import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildStructuredData } from '../../seo/structuredData';
import {
  getCanonicalUrl,
  getLocalizedRouteSeo,
  normalizeSeoLanguage,
} from '../../seo/routes';
import { trackMatomoEvent } from '../../utils/matomo';

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertCanonical = (href: string) => {
  let canonical = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', href);
};

const upsertStructuredData = (pathname: string, language?: string) => {
  const scriptId = 'bluealps-structured-data';
  let script = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.text = JSON.stringify(buildStructuredData(pathname, language));
};

const SeoManager = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  useEffect(() => {
    const routeSeo = getLocalizedRouteSeo(location.pathname, language);
    const canonicalUrl = getCanonicalUrl(location.pathname);
    const htmlLanguage = normalizeSeoLanguage(language);

    document.title = routeSeo.title;
    document.documentElement.lang = htmlLanguage;

    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: routeSeo.description,
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: routeSeo.title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: routeSeo.description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: canonicalUrl,
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: 'https://bluealps.org/logo.webp',
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: 'https://bluealps.org/logo.webp',
    });

    if (routeSeo.noindex) {
      upsertMeta('meta[name="robots"]', {
        name: 'robots',
        content: 'noindex, follow',
      });
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      robots?.remove();
    }

    upsertCanonical(canonicalUrl);
    upsertStructuredData(location.pathname, language);
  }, [language, location.pathname]);

  useEffect(() => {
    const trackedDepths = new Set<number>();
    const scrollDepths = [25, 50, 75, 100];

    const handleScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      if (scrollableHeight <= 0) {
        return;
      }

      const currentDepth = Math.min(
        100,
        Math.round((window.scrollY / scrollableHeight) * 100),
      );

      scrollDepths.forEach((depth) => {
        if (currentDepth >= depth && !trackedDepths.has(depth)) {
          trackedDepths.add(depth);
          trackMatomoEvent(
            'Engagement',
            'scroll_depth',
            `${location.pathname} ${depth}%`,
            depth,
          );
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  return null;
};

export default SeoManager;
